import {
  AssetImage,
  EnumOperatorEnrollmentsOnboardingStatus,
  OperatorByIdDocument,
  useOperatorUpdateMutation,
  useOrganizationsByIdsQuery,
} from "gql/generated";
import { CheckCircle, Hourglass, UnionAlt } from "iconoir-react";
import { useTranslation } from "react-i18next";
import {
  ConnectionCard,
  EmptyState,
  PageContent,
  PageHeader,
  ViewSubSection,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { useOperatorContext } from "../../OperatorContext";
import _ from "lodash";
import { formatPhoneNumber, getMultilingualString } from "shared/utils";
import { Button, useToastContext } from "@ttc3k/trekker";

export const Connections = () => {
  const { t } = useTranslation(["directory", "core"], {
    keyPrefix: "OPERATOR.CONNECTIONS",
  });
  const { toastFactory } = useToastContext();

  const { operator } = useOperatorContext();
  const [updateOperator, { loading: operatorLoading }] =
    useOperatorUpdateMutation();

  const connectedOrgIds = operator.enrollments.map((e) => e?.organization);

  const { data, loading, error } = useOrganizationsByIdsQuery({
    variables: { ids: connectedOrgIds },
  });

  const activeConnectedOrgs = data?.organizationsByIds
    ? _.compact(
        operator.enrollments
          .filter(
            (e) =>
              e?.onboardingStatus ===
              EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
          )
          .map((e) => {
            const fullOrg = data.organizationsByIds.find(
              (org) => org._id === e?.organization,
            );
            return fullOrg;
          }),
      )
    : [];
  const pendingConnectedOrgs = data?.organizationsByIds
    ? _.compact(
        operator.enrollments
          .filter(
            (e) =>
              e?.onboardingStatus !==
              EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
          )
          .map((e) => {
            const fullOrg = data.organizationsByIds.find(
              (org) => org._id === e?.organization,
            );
            return fullOrg;
          }),
      )
    : [];

  const handleConnectOrganization = async (orgId: string) => {
    try {
      /**
       * Update the enrollment status for this org to be complete
       * In the future we can redirect the user to a survery page here instead
       * if needed
       */
      const updatedEnrollmentsArray = operator.enrollments.map((e) => {
        if (e?.organization === orgId) {
          return {
            ...e,
            onboardingStatus:
              EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
          };
        }
        return e;
      });
      await updateOperator({
        variables: {
          entityId: operator._entityId,
          record: {
            enrollments: updatedEnrollmentsArray,
          },
        },
        refetchQueries: [OperatorByIdDocument],
        awaitRefetchQueries: true,
      });
    } catch {
      toastFactory.create({
        title: t("ERROR.TITLE", { ns: "core", keyPrefix: "" }),
        description: t("CONNECT.ERROR"),
      });
    }
  };

  return (
    <Flex flexDir={"column"} gap={"400"}>
      <PageHeader
        title={t("TITLE")}
        description={t("DESCRIPTION")}
        Icon={UnionAlt}
      />
      <PageContent
        loading={loading}
        showSimpleError={!data}
        throwAppError={
          error && (error as unknown as { message: string })?.message
        }
      >
        {activeConnectedOrgs.length < 1 && pendingConnectedOrgs.length < 1 && (
          <EmptyState text={t("EMPTY")} />
        )}
        {activeConnectedOrgs.length > 0 && (
          <ViewSubSection title={t("ACTIVE")} Icon={CheckCircle}>
            {activeConnectedOrgs.map((org) => (
              <ConnectionCard
                key={org._id}
                image={org.logo as AssetImage | undefined | null}
                name={getMultilingualString(org.name)}
                email={org.contact?.email}
                phone={
                  org.contact?.phone
                    ? formatPhoneNumber(org.contact.phone)
                    : undefined
                }
                website={org.contact?.links?.website}
              />
            ))}
          </ViewSubSection>
        )}
        {pendingConnectedOrgs.length > 0 && (
          <ViewSubSection title={t("PENDING")} Icon={Hourglass}>
            {pendingConnectedOrgs.map((org) => (
              <ConnectionCard
                key={org._id}
                image={org.logo as AssetImage | undefined | null}
                name={getMultilingualString(org.name)}
                email={org.contact?.email}
                phone={
                  org.contact?.phone
                    ? formatPhoneNumber(org.contact.phone)
                    : undefined
                }
                website={org.contact?.links?.website}
                ctaOverride={
                  <Button
                    visual={"accent.blue"}
                    mode={"light"}
                    size={"lg"}
                    isLoading={operatorLoading}
                    onClick={() => handleConnectOrganization(org._id)}
                  >
                    {t("CONNECT.TITLE")}
                  </Button>
                }
              />
            ))}
          </ViewSubSection>
        )}
      </PageContent>
    </Flex>
  );
};
